import { FormGroup, ValidationErrors, ValidatorFn, AbstractControl } from '@angular/forms';
import { DateHelper } from '@morningstar/shared/util';
import * as moment from 'moment';

export class MStarValidators {
  static integer: ValidatorFn = (control: FormGroup): ValidationErrors | null => {
    const value = control.value;

    return isNaN(value) || (value % 1 !== 0) ? {'invalidInteger' : 'Number not an integer'} : null;
  }

  static phone: ValidatorFn = (control: FormGroup): ValidationErrors | null => {
    const usPattern = /^\(?([0-9]{3})\)?[ .-]?([0-9]{3})[ .-]?([0-9]{4})$/;
    const internationalPattern = /^\+([0-9]{1,3}) ?([0-9\s\-.()]{4,14})$/;
    const eppInternationalPattern = /^\+[0-9]{1,3}[. ]?[0-9]{4,14}(?:x.+)?$/;

    const invalidPhone = !(usPattern.test(control.value) ||
                         internationalPattern.test(control.value) ||
                         eppInternationalPattern.test(control.value) ||
                         !control.value);

    return invalidPhone ? { 'invalidPhone': 'XXX-XXX-XXXX or +CCC.NNNNNNNNNNxEEEE' } : null;
  }

  static date: ValidatorFn = (control: FormGroup): ValidationErrors | null => {
    if (!control.value) {
      return null;
    }

    const value = moment(control.value);

    const wrongNumberOfDigits = value.year() > 9999 || value.year() < 1000;

    return wrongNumberOfDigits || !value.isValid() ? { invalidDate: true } : null;
  }

  static dateRange: ValidatorFn = (control: FormGroup): ValidationErrors | null => {
    const startDate = control.get('startDate');
    const endDate = control.get('endDate');

    return startDate.value && endDate.value &&
           moment(startDate.value) > moment(endDate.value)
            ? { 'invalidDateRange': 'Start date must be before end date' }
            : null;
  }

  static datesWithinRange(range: number): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (!control.value) {
        return { 'invalidDateRange': 'Select a start and end date' };
      }

      const start = control.value.begin;
      const end = control.value.end;

      if (end < start) {
        return { 'invalidDateRange': 'Start date must be before end date' };
      }

      return DateHelper.differenceInDays(start, end) > range ? { 'invalidDateRange': `date range must be less than or equal to ${range}` } : null;
    };
  }

  /**
   * Built explicitly for the check list settings page
   */
  static checkListRoleAndAssignment: ValidatorFn = (control: FormGroup): ValidationErrors | null => {
    const role = control.get('role').value;
    const assignTo = control.get('assignTo').value;

    return (!role && !assignTo) || (!!role && !!assignTo)
              ? { 'invalidRoleAndAssignment': 'Role or Assign To must be selected, but not both' }
              : null;
  }

  static cellPhoneOrEmailRequired: ValidatorFn = (control: FormGroup): ValidationErrors | null => {
    const cellPhone = control.get('cellPhone').value;
    const email = control.get('email').value;

    return !cellPhone && !email
              ? { 'invalidCellPhoneEmail': 'One of cell phone or email is required' }
              : null;
  }

  static phoneOrEmailRequired: ValidatorFn = (control: FormGroup): ValidationErrors | null => {
    const phone = control.get('phone').value;
    const email = control.get('email').value;

    return !phone && !email
              ? { 'invalidPhoneEmail': 'One of cell phone or email is required' }
              : null;
  }

  /**
   * Built explicitly for the check list settings page
   */
  static frequency: ValidatorFn = (control: FormGroup): ValidationErrors | null => {
    const frequency = control.get('frequency').value;
    const dayOfWeek = control.get('dayOfWeek').value;
    const dayOfMonth = control.get('dayOfMonth').value;

    if (frequency === 'M') {
      return !!dayOfMonth ? null : { 'invalidFrequency': 'Day of month is required' }
    } else if (frequency === 'W') {
      return !!dayOfWeek ? null : { 'invalidFrequency': 'Day of week is required' }
    }
  }

  static lessThanControl(target: string, inclusive: boolean = false): ValidatorFn {
    return (control => {
      const targetControl = control.parent?.get(target);

      if ((!inclusive && control.value >= targetControl?.value) ||
          (inclusive && control.value > targetControl?.value)) {
        return { 'lessThanControl': 'Source must be less than target value' };
      }

      return null;
    });
  }

  static greaterThanControl(target: string, inclusive: boolean = false): ValidatorFn {
    return (control => {
      const targetControl = control.parent?.get(target);

      if ((!inclusive && control.value <= targetControl?.value) ||
          (inclusive && control.value < targetControl?.value)) {
        return { 'greaterThanControl': 'Source must be greater than target value' };
      }

      return null;
    });
  }

  static lessThan(value: number, inclusive: boolean = false): ValidatorFn {
    return (control => {
      if ((!inclusive && control.value >= value) ||
          (inclusive && control.value > value)) {
        return { 'lessThan': 'Source must be less than value' };
      }

      return null;
    });
  }

  static greaterThan(value: number, inclusive: boolean = false): ValidatorFn {
    return (control => {
      if ((!inclusive && control.value <= value) ||
          (inclusive && control.value < value)) {
        return { 'greaterThan': 'Source must be greater than value' };
      }

      return null;
    });
  }

  static sunDefaultValidator: ValidatorFn = (control: FormGroup): ValidationErrors | null => {

    let allows = [
      control.get('allowNextNMonth').value,
      control.get('allowMonthToMonth').value,
      control.get('allow3Month').value,
      control.get('allow6Month').value,
      control.get('allow12Month').value,
      control.get('allow18Month').value,
      control.get('allow24Month').value,
      control.get('allow3Prepaid').value,
      control.get('allow6Prepaid').value,
      control.get('allow12Prepaid').value,
      control.get('allow18Prepaid').value,
      control.get('allow24Prepaid').value
    ];

    let defaults = [
      control.get('defaultNextNMonth').value,
      control.get('defaultMonthToMonth').value,
      control.get('default3Month').value,
      control.get('default6Month').value,
      control.get('default12Month').value,
      control.get('default18Month').value,
      control.get('default24Month').value,
      control.get('default3Prepaid').value,
      control.get('default6Prepaid').value,
      control.get('default12Prepaid').value,
      control.get('default18Prepaid').value,
      control.get('default24Prepaid').value
    ];

    let numberOfDefaults = 0;

    defaults = defaults.filter(d => d);

    if (!allows.some(a => a)) {
      return null;
    }

    return defaults.length === 1
            ? null
            : defaults.length === 0
               ? { 'noDefaultsSelected' : 'A default option must be selected' }
               : { 'tooManyDefaults' : 'Only one option can be the default' };
  }

  /*
  * extracted code from following blog post:
  * https://medium.com/ngx/3-ways-to-implement-conditional-validation-of-reactive-forms-c59ed6fc3325
  * applies/removes validators based on predicate function
  * can use Validators.compose([array of validators]) to apply mutiple validators based on the predicate
  */
  static conditionalValidator(predicate, validator): ValidatorFn {
    return (formControl => {
      if (!formControl.parent) {
        return null;
      }

      if (predicate()) {
        return validator(formControl);
      }

      return null;
    });
  }
}
